// A move is a small record.
//
// It used to be one packed int, so move lists could live in an Int32Array and
// perft would not allocate per node: eight bits of `from`, eight of `to`, and
// the flags above them. Eight bits was exactly an 0x88 square. A square is a
// packed coordinate on an expanding board now and needs twenty, so `from` and
// `to` alone are forty bits — past what JavaScript's bitwise operators can
// even address, let alone hold.
//
// So: fields. The accessors below kept their names and shapes, which is why
// almost nothing outside this file had to change; `isCapture(m)` reads the
// same whether the bit is in a word or a field. Perft allocates per move now
// and is slower for it, and that is the right trade — the alternative is an
// encoding that silently truncates a square the moment the board grows past
// eight files.

import {
  pieceColor,
  type PieceType,
  pieceType,
  type Side,
  squareName,
  STRIDE,
  WHITE
} from "./board";

export const FLAG_CAPTURE = 1 << 0;
export const FLAG_EP = 1 << 1;
export const FLAG_CASTLE = 1 << 2;
export const FLAG_DOUBLE = 1 << 3;
/**
 * The capture took a *structure*, not a piece (§26 F5).
 *
 * `captured` stays 0 — there is no piece type to put back — and `capturedSide`
 * carries the building's owner, which is what unmake needs to restore the
 * mirror. A flag rather than a sentinel piece type, because every reader of
 * `moveCapturedType` indexes value tables by it and a sentinel would walk off
 * the end of each one in silence.
 */
export const FLAG_STRUCTURE = 1 << 4;
/**
 * A **bash**: a capture whose blow did not finish its target (§27).
 *
 * The target loses health and is shoved a square along the blow, or stands its
 * ground and the attacker stops short of it. Either way nothing was captured,
 * so `captured` stays 0 and `isCapture` is false — which is what keeps the run
 * tally, the loot roll and the altar claim correctly out of it without any of
 * them being told this flag exists.
 *
 * **A move rather than a command**, unlike `lance`, and that is the whole
 * architecture of §27: a blow decides which moves are *legal* in both
 * directions. A pawn that bounces off the checking queen has not resolved the
 * check, and a shove that pushes the checker off its line has — and
 * `generateLegalMoves` answers both by making and unmaking candidates, so the
 * knockback has to happen inside `makeMove` or the filter tests a board the
 * game never reaches.
 */
export const FLAG_BASH = 1 << 5;
/**
 * A **ring out** (§31 K4): a blow that would not have finished its target, but
 * whose shove lands in the void, so the target is destroyed instead. Rides on
 * `FLAG_CAPTURE` — a piece came off the board, its corpse lands where the
 * attacker now stands (design example 3) — so every capture reader is right
 * untold; this bit is for the tally (`ringOuts`) and the renderer.
 */
export const FLAG_RING_OUT = 1 << 6;

export interface Move {
  from: number;
  to: number;
  /** 0 = not a promotion. */
  promotion: PieceType | 0;
  /** What was taken, which unmake needs to put back. 0 = nothing. */
  captured: PieceType | 0;
  /**
   * Which army the captured piece belonged to.
   *
   * Unmake used to reconstruct it as `opposite(mover)`, which is only right
   * while there are exactly two armies — with three it puts the piece back
   * under the wrong flag, and nothing throws.
   */
  capturedSide: Side;
  flags: number;
  /**
   * The square the target of a bash is standing on, or -1.
   *
   * `to` stays **where the attacker ends up**, because every reader of
   * `moveTo` means that — `migratePiece`, the tally, the
   * renderer. For a bash the two differ whenever the shove was refused: the
   * attacker stops short and the target is still on `shoved`.
   *
   * `Move` is a record rather than a packed int, so a field is free — which is
   * the reason `move.ts` says it stopped being one.
   */
  shoved: number;
  /** The square a Hydraulic Clasp drags a second piece off, or -1 (§26 C11). */
  dragged: number;
  /**
   * Squares whose **non-obstruction** structure this move destroys (§31 S1):
   * plowed along a non-jumping path, crushed where the mover lands, broken
   * where a shoved piece lands. Board order of travel. `NO_BREAK` when none —
   * shared and frozen, so an ordinary move allocates nothing.
   */
  broken: readonly number[];
  /**
   * Structure HP the mover plowed through **before** its target (§31 S1) —
   * what comes off its blow (`captureDamage − spent`). 0 for every move that
   * crossed nothing; never counts a crush on the landing square.
   */
  spent: number;
}

/** The empty `broken` list every ordinary move shares. Never written. */
export const NO_BREAK: readonly number[] = Object.freeze([]);

/** Does this move destroy any non-obstruction structure? */
export const breaksStructures = (m: Move): boolean => m.broken.length > 0;

export function createMove(
  from: number,
  to: number,
  flags = 0,
  promotion: PieceType | 0 = 0,
  captured: PieceType | 0 = 0,
  capturedSide: Side = 0
): Move {
  return { from, to, promotion, captured, capturedSide, flags, shoved: -1, dragged: -1, broken: NO_BREAK, spent: 0 };
}

/**
 * A blow that did not finish its target.
 *
 * `to` is where the attacker ends up — the target's square when the shove
 * lands, one short of it when the shove is refused — and `shoved` is where the
 * target is standing when the blow is struck. Where it *goes* is derived, not
 * stored: it is one step further along the same blow, which `shoveStep` reads
 * back off `from` and `shoved`.
 */
export function bashOnto(from: number, to: number, target: number, shoved: number): Move {
  return {
    from,
    to,
    promotion: 0,
    // **Nothing was taken.** A bash that filed a `captured` type would be
    // scored as a kill by the tally, spill a corpse, and roll loot for a piece
    // still standing on the board.
    captured: 0,
    capturedSide: pieceColor(target),
    // **`FLAG_BASH` alone, so `isCapture` is false.** Nothing was taken, and
    // every reader of that flag means "a piece came off the board": the run
    // tally, the loot roll, the altar claim and `applyMove`'s spill. Including
    // it would leave a corpse for a piece still standing there.
    //
    // It costs nothing in `generateLegalCaptures`, which filters at generation
    // — the emitters ask `canTake` and never read this flag — so a bash is
    // still offered to the reply search, which is what H4 requires.
    flags: FLAG_BASH,
    shoved,
    dragged: -1,
    broken: NO_BREAK,
    spent: 0
  };
}

/**
 * A blow at a building that did not finish it (§27 + §26 F5).
 *
 * `FLAG_BASH | FLAG_STRUCTURE`, exactly parallel to a lethal one carrying
 * `FLAG_CAPTURE | FLAG_STRUCTURE`: the structure flag says *what* was hit and
 * the other says whether it came off the board. That is what keeps every
 * existing reader of `isStructureCapture` meaning "a building was taken"
 * without any of them being told this exists.
 *
 * **A building is never shoved**, so there is no landing to choose: `to` is
 * where the attacker stops — `bounceSquare`, which is its own square when it
 * was already adjacent — and `shoved` names the building's square, which is
 * what `moveTarget` and `findLegalMove` match on.
 *
 * `capturedSide` is passed rather than read off a piece: there is no piece,
 * and `pieceColor(0)` is a plausible wrong answer that never throws.
 */
export function bashStructure(
  from: number,
  to: number,
  at: number,
  owner: Side
): Move {
  return {
    from,
    to,
    promotion: 0,
    captured: 0,
    capturedSide: owner,
    flags: FLAG_BASH | FLAG_STRUCTURE,
    shoved: at,
    dragged: -1,
    broken: NO_BREAK,
    spent: 0
  };
}

/** A move onto `target`, which may be empty — captures encode what they took. */
export function moveOnto(
  from: number,
  to: number,
  target: number,
  promotion: PieceType | 0 = 0
): Move {
  return target === 0
    ? createMove(from, to, 0, promotion)
    : createMove(from, to, FLAG_CAPTURE, promotion, pieceType(target), pieceColor(target));
}

/** A capture of an enemy structure — `capturedSide` is the building's owner. */
export function moveOntoStructure(
  from: number,
  to: number,
  owner: Side,
  promotion: PieceType | 0 = 0
): Move {
  return createMove(from, to, FLAG_CAPTURE | FLAG_STRUCTURE, promotion, 0, owner);
}

export const moveFrom = (m: Move): number => m.from;
export const moveTo = (m: Move): number => m.to;
export const movePromotion = (m: Move): PieceType | 0 => m.promotion;
export const moveCapturedType = (m: Move): PieceType | 0 => m.captured;
export const moveCapturedSide = (m: Move): Side => m.capturedSide;
export const isCapture = (m: Move): boolean => (m.flags & FLAG_CAPTURE) !== 0;
/** Did this blow fail to finish its target? See `FLAG_BASH`. */
export const isBash = (m: Move): boolean => (m.flags & FLAG_BASH) !== 0;
/** Was this capture a ring out? See `FLAG_RING_OUT`. */
export const isRingOut = (m: Move): boolean => (m.flags & FLAG_RING_OUT) !== 0;
/** Does this move drag a clamped second piece along with it? See `dragged`. */
export const isDrag = (m: Move): boolean => m.dragged >= 0;
/**
 * Does this move go *at* an enemy piece, whether or not it takes one?
 *
 * **`isCapture` was doing this job and stopped being able to.** A bash is an
 * attack that captures nothing, so it carries `FLAG_BASH` and not
 * `FLAG_CAPTURE` — which is right for every reader that means "a piece came
 * off the board" (the tally, the loot roll, the spill) and wrong for every
 * reader that means "this move is aggressive".
 *
 * Found by driving it: `emitImitated` drops captures because Imitate is a
 * *movement* card, and a bash slipped through the filter — so a pawn copying
 * a rook could shove a piece down the file it had only borrowed the walk of.
 */
export const isAttack = (m: Move): boolean => isCapture(m) || isBash(m);

/**
 * The square that **names** this move — what a player clicks, and what a
 * `{ type: "move" }` command has to carry.
 *
 * `moveTo` is where the piece ends up, and for a bash that is not the same
 * question: the attacker stops one square short whenever the shove was
 * refused, and a knight that has no approach to stop on does not move at all —
 * so `moveTo` answers its own square and the command reads `from === to`,
 * which resolves to nothing. Measured as the planner proposing illegal moves.
 *
 * Every builder of a move command goes through this. `findLegalMove` is the
 * other half and matches a bash on exactly this square.
 */
export const moveTarget = (m: Move): number => (isBash(m) ? m.shoved : m.to);
/** Does this move go *at* a building, whether or not it takes one? */
export const isStructureBlow = (m: Move): boolean => (m.flags & FLAG_STRUCTURE) !== 0;
/**
 * Did a building come off the board?
 *
 * **The capture flag as well as the structure one**, since §27 gave a building
 * health: a blow short of it is a bash and takes nothing, and every reader of
 * this means the wreck — `applyMove`'s `wreckStructure`, `makeMove`'s cell
 * restore, the halfmove reset, the planner's spoils. Exactly the split
 * `isCapture` already draws for pieces, where a bash carries `FLAG_BASH` and
 * never `FLAG_CAPTURE`.
 */
export const isStructureCapture = (m: Move): boolean =>
  (m.flags & (FLAG_CAPTURE | FLAG_STRUCTURE)) === (FLAG_CAPTURE | FLAG_STRUCTURE);
export const isEnPassant = (m: Move): boolean => (m.flags & FLAG_EP) !== 0;
export const isCastle = (m: Move): boolean => (m.flags & FLAG_CASTLE) !== 0;
export const isDoublePush = (m: Move): boolean => (m.flags & FLAG_DOUBLE) !== 0;

/** The square this move acts on: not `to` for an en passant or a bash. */
export const victimOf = (m: Move, color: Side): number =>
  isBash(m)
    ? m.shoved
    : isEnPassant(m)
      ? m.to + (color === WHITE ? -STRIDE : STRIDE)
      : m.to;

export const sameMove = (a: Move, b: Move): boolean =>
  a.from === b.from && a.to === b.to && a.promotion === b.promotion;

/**
 * Long algebraic (e2e4, e7e8q) — for logs, tests and the command log.
 *
 * Outside the classic eight-by-eight the square names are `f<file>r<rank>`
 * rather than letters, so this stops being UCI in any strict sense. It is a
 * readable identifier, and the command log is the actual protocol.
 */
export function moveToUci(m: Move): string {
  return (
    squareName(m.from) + squareName(m.to) + (m.promotion ? ".pnbrqk"[m.promotion] : "")
  );
}
